import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismComboSelectInput,
  PrismSelectInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import EmberPopover from '@mylighthouse/prism-ember/components/ember-popover';
import { t, tKey } from 'ember-intl';
import { eq, not } from 'ember-truth-helpers';
import { PLATFORMS } from 'frontend-utils/utils/constants/constant-values';
import {
  AVAILABLE_RATECHANGES,
  COMPARETYPES,
  COMPARETYPES_TO_QUERYPARAMS,
  MEMBERSHIPTYPES,
  RATE_INSIGHT_FILTER_TYPE_RATE_PARAM,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';

interface MarketRatesButtonsButtonCompareSignature {
  Args: {
    availableLos: unknown;
    availableMealPlans: unknown;
    availableMembershipTypeFilters: unknown;
    availablePlatforms: unknown;
    availableRoomTypes: unknown;
    compareDisabledTooltipText: unknown;
    hasMealplanFilter: unknown;
    hasMembershipTypeFilter: unknown;
    hasPersonsFilter: unknown;
    hasRoomtypeFilter: unknown;
    isCompareDisabled: unknown;
    maxPersonsOptions: unknown;
    rateParams: unknown;
  };
}

export default class MarketRatesButtonsButtonCompare extends Component<MarketRatesButtonsButtonCompareSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isPopoverVisible = false;
  @tracked selectedComparison = null;
  @tracked selectedFilter = null;

  // Build second dropdown options (compareOptions) depending on the selected option in first dropdown (selectedFilter).
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get compareOptions() {
    // Return null if no filter is selected in first dropdown.
    if (!this.selectedFilter) {
      return null;
    }

    const compareOptions = [];

    // @ts-expect-error: Incorrect type
    switch (this.selectedFilter.type) {
      case RATE_INSIGHT_FILTER_TYPE_RATE_PARAM:
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.rateParams
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter((filter) => this.selectedFilter.id !== filter.id)
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .forEach((rateParam) => {
            compareOptions.push({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              id: `compare_rate_param_${rateParam.id}`,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              label: rateParam.label,
              compareType: COMPARETYPES.RATE_PARAM,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              compareValue: rateParam.id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              icon: rateParam.icon,
            });
          });
        break;

      case COMPARETYPES.MEMBERSHIPTYPE: {
        // Allow Public vs All (and All vs Public)
        // @ts-expect-error: Incorrect type
        if (this.selectedFilter.id === MEMBERSHIPTYPES.PUBLIC) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.args.availableMembershipTypeFilters
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .filter((filter) => this.selectedFilter.id !== filter.id)
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .forEach((membershipType) => {
              compareOptions.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                id: `compare_membership_${membershipType.id}`,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                label: membershipType.label,
                compareType: COMPARETYPES.MEMBERSHIPTYPE,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                compareValue: membershipType.id,
              });
            });
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const publicMembershipType =
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.args.availableMembershipTypeFilters.find(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (filter) => filter.id === MEMBERSHIPTYPES.PUBLIC,
            );
          compareOptions.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            id: `compare_membership_${publicMembershipType.id}`,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            label: publicMembershipType.label,
            compareType: COMPARETYPES.MEMBERSHIPTYPE,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            compareValue: publicMembershipType.id,
          });
        }
        break;
      }

      case COMPARETYPES.MEALTYPE:
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.availableMealPlans
          .filter(
            // @ts-expect-error: Incorrect type
            (filter) =>
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
              filter.isComparable && this.selectedFilter.id !== filter.id,
          )
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .forEach((mealType) => {
            compareOptions.push({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              id: `compare_mealplan_${mealType.id}`,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              label: mealType.label,
              compareType: COMPARETYPES.MEALTYPE,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              compareValue: mealType.id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              icon: mealType.icon,
            });
          });
        break;

      case COMPARETYPES.LOS:
        // LOS: compare to LOS = 1 when LOS > 1
        // @ts-expect-error: Incorrect type
        if (this.selectedFilter.id > 1) {
          compareOptions.push({
            id: 'compare_los_1',
            label: this.intl.t('rates.nights', { count: 1 }),
            compareType: COMPARETYPES.LOS,
            compareValue: '1',
          });
        }
        break;

      case COMPARETYPES.PERSONS:
        // Allow Default vs All (and All vs Default)
        // @ts-expect-error: Incorrect type
        if (this.selectedFilter.id === this.hotelOrMarket.defaultPersons) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.args.maxPersonsOptions
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .filter((filter) => this.selectedFilter.id !== filter.id)
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .forEach((person) => {
              compareOptions.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                id: `compare_persons_${person.id}`,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                label: person.label,
                compareType: COMPARETYPES.PERSONS,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                compareValue: `${person.id}`,
              });
            });
        } else {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
          const defaultNumberOfGuestsOption = this.args.maxPersonsOptions.find(
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (filter) => filter.id === this.hotelOrMarket.defaultPersons,
          );
          compareOptions.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            id: `compare_persons_${defaultNumberOfGuestsOption.id}`,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            label: defaultNumberOfGuestsOption.label,
            compareType: COMPARETYPES.PERSONS,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            compareValue: `${defaultNumberOfGuestsOption.id}`,
          });
        }
        break;

      case COMPARETYPES.PLATFORM:
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.args.availablePlatforms
          // We won’t support mobile web vs mobile app supports as this logic is not foreseen in the feature
          // @ts-expect-error: Incorrect type
          .filter((filter) => {
            // @ts-expect-error: Incorrect type
            if (this.selectedFilter.id === PLATFORMS.PLATFORM_MOBILE_WEBSITE) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              return filter.id !== PLATFORMS.PLATFORM_MOBILE_APP;
            }
            // @ts-expect-error: Incorrect type
            if (this.selectedFilter.id === PLATFORMS.PLATFORM_MOBILE_APP) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              return filter.id !== PLATFORMS.PLATFORM_MOBILE_WEBSITE;
            }
            return true;
          })
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .filter((filter) => this.selectedFilter.id !== filter.id)
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .forEach((platform) => {
            compareOptions.push({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              id: `compare_platform_${platform.id}`,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              label: platform.label,
              compareType: COMPARETYPES.PLATFORM,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              compareValue: platform.id,
            });
          });
        break;

      case COMPARETYPES.ROOMTYPE:
        // Only for hotel view!
        if (this.hotelAndMarketService.hotel) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.args.availableRoomTypes
            .filter(
              // @ts-expect-error: Incorrect type
              (filter) =>
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
                filter.isComparable && this.selectedFilter.id !== filter.id,
            )
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .forEach((roomType) => {
              compareOptions.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                id: `compare_roomtype_${roomType.id}`,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                label: roomType.label,
                compareType: COMPARETYPES.ROOMTYPE,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                compareValue: `${roomType.id}`,
              });
            });
        }
        break;

      case COMPARETYPES.DATE:
        AVAILABLE_RATECHANGES.forEach((rateChangeDay) => {
          const compareValueForDay = this.hotelAndMarketService
            .getLocalTime()
            .startOf('day')
            .subtract(rateChangeDay, 'day')
            .format('YYYY-MM-DD');
          compareOptions.push({
            id: rateChangeDay,
            label: this.intl.t('general.daysago', { count: rateChangeDay }),
            compareType: COMPARETYPES.DATE,
            compareValue: compareValueForDay,
          });
        });

        compareOptions.push({
          id: COMPARETYPES.SWLY,
          label: this.intl.t('general.versus.swly_short'),
          compareType: COMPARETYPES.SWLY,
          compareValue: 'true',
        });
        break;

      default:
        break;
    }

    return compareOptions;
  }

  // Array of possible option groups with their condition to be added to the navigable select dropdown.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterConditionals() {
    return [
      {
        condition: true,
        groupNameTranslationKey: tKey('rates.rate_types'),
        options: this.args.rateParams,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        condition: this.hasPlatformCompare,
        groupNameTranslationKey: tKey('rates.platforms'),
        options: this.args.availablePlatforms,
      },
      {
        condition: this.args.hasMembershipTypeFilter,
        groupNameTranslationKey: tKey('rates.memberships'),
        options: this.args.availableMembershipTypeFilters,
      },
      {
        condition: true,
        groupNameTranslationKey: 'rates.los',
        options: this.args.availableLos,
      },
      {
        condition: this.args.hasPersonsFilter,
        groupNameTranslationKey: 'rates.guests',
        options: this.args.maxPersonsOptions,
      },
      {
        condition: this.args.hasRoomtypeFilter,
        groupNameTranslationKey: tKey('rates.roomtypes'),
        options: this.args.availableRoomTypes,
      },
      {
        condition: this.args.hasMealplanFilter,
        groupNameTranslationKey: tKey('rates.meal_types'),
        options: this.args.availableMealPlans,
      },
    ];
  }

  // Returns option groups in the correct format so the navigable select dropdown can use it.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get groupedFilterOptions() {
    const groupedFilterOptions = [];

    // Loop over option group conditions and add option group if condition is fulfilled and there are multiple options.
    this.filterConditionals.forEach((conditional) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const comparableOptions = conditional.options.filter(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        (option) => option.isComparable,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (conditional.condition && comparableOptions.length > 0) {
        groupedFilterOptions.push({
          groupName: this.intl.t(conditional.groupNameTranslationKey),
          unsafe_isCollapsed: true,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          options: comparableOptions,
        });
      }
    });

    // Date compare is a unique option as it has no nested options
    groupedFilterOptions.push({
      label: this.intl.t('rates.compare.selected_date'),
      type: COMPARETYPES.DATE,
    });

    return groupedFilterOptions;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPlatformCompare() {
    // @ts-expect-error: Incorrect type
    const { rateParam } = this.router.currentRoute.queryParams;
    // Don't add Platform to the list of options when 3rd Party is selected (Mobile + 3rd Party is not supported)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      this.args.availablePlatforms.length &&
      rateParam !== RATE_PARAMS.THIRD_PARTY
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // Returns true if selectedFilter and selectedComparison are not null.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get validateCompare() {
    return this.selectedFilter && this.selectedComparison;
  }

  @action
  async applyCompare(): Promise<void> {
    // @ts-expect-error: Incorrect type
    const { compareType, compareValue } = this.selectedComparison;
    // @ts-expect-error: Incorrect type
    const { type, id } = this.selectedFilter;

    // The QPs for the comparison.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const queryParams = { compareType, compareValue };

    if (type !== COMPARETYPES.DATE && type !== COMPARETYPES.SWLY) {
      // We can update the filters using the compare menu but we never update the month QP
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      queryParams[COMPARETYPES_TO_QUERYPARAMS[type]] = id;
    }

    // Apply the filter and compare via QPs.
    await this.router.transitionTo({ queryParams });

    this.hidePopover();

    this.usageAnalytics.track('Daily Rates Compared', {
      eventTrigger: 'table-view',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      compareType,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      compareValueFrom: compareValue.toString(),
      compareValueTo: `${id}`,
    });
  }

  // Clear selected dropdown items.
  @action
  handleShow(): void {
    // @ts-expect-error: Incorrect type
    const { view } = this.router.currentRoute.queryParams;

    if (view === 'table') {
      // Testing out the new tracking service, eventually one of these ⬆️⬇️ tracking calls will be removed
      this.usageAnalytics.track('Daily Rates Comparing', {
        eventTrigger: 'table-view',
      });
    }

    this.selectedFilter = null;
    this.selectedComparison = null;
  }

  // Handler for Ember Popover onShow event.
  @action
  hidePopover(): void {
    this.isPopoverVisible = false;
  }

  @action
  // @ts-expect-error: Incorrect type
  setSelectedFilter(filter): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectedFilter = filter;
    this.updateSelectedComparison();
  }

  // If there is only one compare option, select it.
  // Otherwise clear the selected option in compare dropdown.
  @action
  updateSelectedComparison(): void {
    if (this.compareOptions?.length === 1) {
      // @ts-expect-error: Incorrect type
      this.selectedComparison = this.compareOptions[0];
    } else {
      this.selectedComparison = null;
    }
  }

  <template>
    <div class="button-compare flex items-center">
      <PrismButton
        @buttonType="secondary"
        @iconBefore="compare"
        {{! @glint-expect-error: Incorrect type }}
        @isDisabled={{@isCompareDisabled}}
        @label={{t "general.compare"}}
      />
      <EmberPopover
        @animationDuration={{0}}
        @event="click"
        @isShown={{this.isPopoverVisible}}
        @onShow={{this.handleShow}}
        @popoverHideDelay={{0}}
        {{! @glint-expect-error: Incorrect type }}
        @side="bottom-end"
        @spacing={{0}}
        @tooltipClass="button-compare__popover"
      >
        <div class="button-compare__popover__filters">
          <div class="button-compare__popover__filters__label">
            {{t "rates.compare.create_a_comparison"}}
          </div>

          <div
            class="button-compare__popover__filters__dropdowns flex flex-row"
          >
            <div
              class="button-compare__popover__filters__dropdowns__dropdown flex flex-col"
            >
              <PrismComboSelectInput
                @onChange={{this.setSelectedFilter}}
                @options={{this.groupedFilterOptions}}
                @placeholder={{t "general.select_a_value"}}
                @unsafe_collapsibleGroupBackLabel={{t
                  "rates.compare.back_to_categories"
                }}
                @unsafe_renderInPlace={{true}}
                @value={{this.selectedFilter}}
                {{! Prevents the parent dropdown from closing when selecting a value, this fix should be temporary once we have a new dropdown component }}
              />
            </div>

            <div class="button-compare__popover__filters__dropdowns__vs">{{t
                "revenue_insight.vs"
              }}</div>

            <div
              class="button-compare__popover__filters__dropdowns__dropdown flex flex-col"
            >
              <PrismSelectInput
                @disabled={{eq this.compareOptions null}}
                @disabledMessage={{t "rates.compare.please_select_value_first"}}
                @onChange={{fn (mut this.selectedComparison)}}
                {{! @glint-expect-error: Incorrect type }}
                @options={{this.compareOptions}}
                @placeholder={{t "rates.compare.select_comparison"}}
                @unsafe_renderInPlace={{true}}
                @value={{this.selectedComparison}}
                {{! Prevents the parent dropdown from closing when selecting a value, this fix should be temporary once we have a new dropdown component }}
              />
            </div>
          </div>
        </div>
        <div class="button-compare__popover__actions flex flex-row">
          <div class="button-compare__popover__actions__buttons flex flex-row">
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.hidePopover}}
            />
            <PrismButton
              @isDisabled={{not this.validateCompare}}
              @label={{t "general.apply"}}
              @onClick={{this.applyCompare}}
            />
          </div>
        </div>
      </EmberPopover>

      {{#if @isCompareDisabled}}
        {{! @glint-expect-error: Incorrect type }}
        <PrismTooltip @text={{@compareDisabledTooltipText}} />
      {{/if}}
    </div>
  </template>
}
